import { useMemo, useState } from 'react'
import { useCart } from '../../../cart'
import type { CartItem } from '../../../cart'

export const useCartPageModel = () => {
  const {
    cart,
    cartId,
    itemCount,
    subtotal,
    busy,
    initializing,
    error,
    clearError,
    updateItemQuantity,
    removeItem,
    clearCart,
  } = useCart()
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  const sortedItems = useMemo(
    () => [...(cart?.items ?? [])].sort((left, right) => left.productName.localeCompare(right.productName)),
    [cart?.items],
  )

  const withItemBusy = async (itemId: string, operation: () => Promise<boolean>) => {
    setActiveItemId(itemId)
    try {
      await operation()
    } finally {
      setActiveItemId((current) => (current === itemId ? null : current))
    }
  }

  const handleIncrease = (item: CartItem) =>
    withItemBusy(item.itemId, () => updateItemQuantity(item.itemId, item.quantity + 1))

  const handleDecrease = (item: CartItem) =>
    withItemBusy(item.itemId, () =>
      item.quantity <= 1 ? removeItem(item.itemId) : updateItemQuantity(item.itemId, item.quantity - 1),
    )

  const handleRemove = (item: CartItem) => withItemBusy(item.itemId, () => removeItem(item.itemId))

  return {
    cartId,
    itemCount,
    subtotal,
    busy,
    initializing,
    error,
    clearError,
    sortedItems,
    clearCart,
    handleIncrease,
    handleDecrease,
    handleRemove,
    isItemBusy: (itemId: string) => busy && activeItemId === itemId,
  }
}
